"use client";

import { useState, useCallback, memo, useMemo, useRef, useEffect, useEffectEvent, useSyncExternalStore } from "react";
import { useAudioEngineState, useAudioEngineTicker, useAudioEngine, useCurrentTimestamp } from "../react/hooks";
import { TrackState } from "../types";
import { CommandLogEntry, StateDiff } from "../CommandLog";
import { useDevTools } from "./DevToolsContext";
const MIN_HEIGHT = 50;
const MAX_HEIGHT = 500;
const DEFAULT_HEIGHT = 400;
const MIN_WIDTH = 200;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 400;
type Tab = "state" | "commands";
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor(seconds % 1 * 1000);
  return `${mins}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
}

// ============================================================================
// Isolated timestamp components (re-render on every tick)
// ============================================================================

function CurrentTimeRow() {
  const currentTimestamp = useCurrentTimestamp();
  return <Row label="Time" value={formatTime(currentTimestamp)} data-sentry-element="Row" data-sentry-component="CurrentTimeRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function ProgressRow() {
  const currentTimestamp = useCurrentTimestamp();
  const duration = useAudioEngineState(s => s.duration);
  const progress = duration > 0 ? Math.round(currentTimestamp / duration * 100) : 0;
  return <Row label="Progress" value={`${progress}%`} data-sentry-element="Row" data-sentry-component="ProgressRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function useAudioEngineDebugData() {
  return useAudioEngineState((state, engine) => {
    const debugState = engine.getDebugState();
    return {
      activeBackend: debugState.backend.activeBackend,
      regionMetadata: debugState.regions,
      regions: state.regions,
      tracks: state.tracks,
      waveforms: state.waveforms
    };
  });
}
function useStreamingBufferDebugData() {
  useAudioEngineTicker(s => s.currentTimestamp);
  const engine = useAudioEngine();
  return engine.getDebugState().backend;
}
function formatBufferDuration(seconds: number | null): string {
  return seconds === null ? "—" : `${seconds.toFixed(2)}s`;
}
type RegionStreamingStatus = "stream-ready" | "streamable-decoded" | "blocked";
function getRegionStreamingDetails(region: DebugRegionRowState): {
  label: string;
  status: RegionStreamingStatus;
} {
  if (region.pendingUpload) {
    return {
      label: "blocked: pending upload",
      status: "blocked"
    };
  }
  if (region.codec !== "opus") {
    return {
      label: "blocked: needs Opus",
      status: "blocked"
    };
  }
  if (!region.hasAudioBlob) {
    return {
      label: "blocked: missing encoded audio",
      status: "blocked"
    };
  }
  if (region.hasAudioBuffer || !region.hasWaveformKey) {
    return {
      label: "streamable + decoded",
      status: "streamable-decoded"
    };
  }
  return {
    label: "stream-ready",
    status: "stream-ready"
  };
}

// ============================================================================
// Isolated status badges (each subscribes to its own state)
// ============================================================================

const StatusBadges = memo(function StatusBadges() {
  return <div className="flex gap-1.5 mb-3 flex-wrap">
      <BackendBadge />
      <ReadyBadge />
      <PlayingBadge />
      <SeekingBadge />
      <RecordingBadge />
    </div>;
});
function BackendBadge() {
  const {
    activeBackend
  } = useAudioEngineDebugData();
  const label = `Backend: ${activeBackend ?? "—"}`;
  const activeClassName = activeBackend === "streaming" ? "bg-cyan-600" : "bg-neutral-600";
  return <StatusBadge label={label} active={activeBackend !== null} activeClassName={activeClassName} data-sentry-element="StatusBadge" data-sentry-component="BackendBadge" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function ReadyBadge() {
  const ready = useAudioEngineState(s => s.ready);
  return <StatusBadge label="Ready" active={ready} activeClassName="bg-green-500" data-sentry-element="StatusBadge" data-sentry-component="ReadyBadge" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function PlayingBadge() {
  const playing = useAudioEngineState(s => s.playing);
  return <StatusBadge label="Playing" active={playing} activeClassName="bg-blue-500" data-sentry-element="StatusBadge" data-sentry-component="PlayingBadge" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function SeekingBadge() {
  const seeking = useAudioEngineState(s => s.seeking);
  return <StatusBadge label="Seeking" active={seeking} activeClassName="bg-amber-500" data-sentry-element="StatusBadge" data-sentry-component="SeekingBadge" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function RecordingBadge() {
  const recording = useAudioEngineState(s => s.recording);
  return <StatusBadge label="Recording" active={recording} activeClassName="bg-red-500" data-sentry-element="StatusBadge" data-sentry-component="RecordingBadge" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}

// ============================================================================
// Isolated data sections
// ============================================================================

function DurationRow() {
  const duration = useAudioEngineState(s => s.duration);
  return <Row label="Duration" value={formatTime(duration)} data-sentry-element="Row" data-sentry-component="DurationRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function MasterGainRow() {
  const masterGain = useAudioEngineState(s => s.masterGain);
  return <Row label="Master" value={`${Math.round(masterGain * 100)}%`} data-sentry-element="Row" data-sentry-component="MasterGainRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function PlaybackRateRow() {
  const playbackRate = useAudioEngineState(s => s.playbackRate);
  return <Row label="Speed" value={`${playbackRate}x`} data-sentry-element="Row" data-sentry-component="PlaybackRateRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function TransposeRow() {
  const transpose = useAudioEngineState(s => s.transpose);
  const label = transpose === 0 ? "0 st" : transpose > 0 ? `+${transpose} st` : `${transpose} st`;
  return <Row label="Transpose" value={label} data-sentry-element="Row" data-sentry-component="TransposeRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function PitchShiftBypassRow() {
  const playbackRate = useAudioEngineState(s => s.playbackRate);
  const transpose = useAudioEngineState(s => s.transpose);
  // NOTE: This is re-calculated here to avoid needing to expose it from the player
  const rateCorrection = -12 * Math.log2(playbackRate);
  const bypassed = Math.abs(rateCorrection + transpose) < 0.01;
  return <Row label="Pitch shift" value={bypassed ? "Bypassed" : "Active"} data-sentry-element="Row" data-sentry-component="PitchShiftBypassRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function RecordingStartPositionRow() {
  const recordingStartPosition = useAudioEngineState(s => s.recordingStartPosition);
  return <Row label="Start position" value={recordingStartPosition !== null ? formatTime(recordingStartPosition) : "—"} data-sentry-element="Row" data-sentry-component="RecordingStartPositionRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function PendingRegionsCountRow() {
  const regions = useAudioEngineState(s => s.regions);
  const pendingCount = Object.values(regions).filter(r => r.pendingUpload).length;
  return <Row label="Pending regions" value={String(pendingCount)} data-sentry-element="Row" data-sentry-component="PendingRegionsCountRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
function SelectedInputDeviceRow() {
  const selectedInputDeviceId = useAudioEngineState(s => s.selectedInputDeviceId);
  return <div className="flex justify-between py-0.5 text-[11px]" data-sentry-component="SelectedInputDeviceRow" data-sentry-source-file="AudioEngineDevTools.tsx">
      <span className="text-neutral-400">Input device</span>
      <span className="text-neutral-200 font-mono truncate max-w-[120px]" title={selectedInputDeviceId ?? undefined}>
        {selectedInputDeviceId ?? "None"}
      </span>
    </div>;
}
function InputLevelRow() {
  const inputMonitoring = useAudioEngineState(s => s.inputMonitoring);
  const inputLevel = useAudioEngineTicker(s => s.inputLevel);
  if (!inputMonitoring) {
    return <Row label="Input level" value="—" />;
  }
  const levelPercent = Math.round(inputLevel * 100);
  return <Row label="Input level" value={`${levelPercent}%`} data-sentry-element="Row" data-sentry-component="InputLevelRow" data-sentry-source-file="AudioEngineDevTools.tsx" />;
}
const RecordingSection = memo(function RecordingSection() {
  const recording = useAudioEngineState(s => s.recording);
  return <Section title="Recording">
      <Row label="Status" value={recording ? "Recording..." : "Idle"} />
      <RecordingStartPositionRow />
      <SelectedInputDeviceRow />
      <InputLevelRow />
      <PendingRegionsCountRow />
    </Section>;
});
const TracksSection = memo(function TracksSection() {
  const tracks = useAudioEngineState(s => s.tracks);
  const regions = useAudioEngineState(s => s.regions);
  const trackEntries = Object.entries(tracks);

  // Calculate which tracks have pending regions
  const tracksPendingStatus = useMemo(() => {
    const status: Record<string, boolean> = {};
    for (const trackId of Object.keys(tracks)) {
      status[trackId] = Object.values(regions).some(r => r.trackId === trackId && r.pendingUpload);
    }
    return status;
  }, [tracks, regions]);
  return <Section title={`Tracks (${trackEntries.length})`}>
      {trackEntries.length === 0 ? <div className="text-neutral-500 italic">No tracks loaded</div> : trackEntries.map(([id, trackState]) => <TrackRow key={id} id={id} state={trackState} hasPendingRegions={tracksPendingStatus[id] ?? false} />)}
    </Section>;
});
const WaveformsSection = memo(function WaveformsSection() {
  const waveformCount = useAudioEngineState(s => Object.keys(s.waveforms).length);
  return <Section title={`Waveforms (${waveformCount})`}>
      <div className="text-neutral-400 text-[11px]">
        {waveformCount === 0 ? "No waveforms loaded" : `${waveformCount} region(s) with waveform data`}
      </div>
    </Section>;
});
const StreamingBuffersSection = memo(function StreamingBuffersSection() {
  const tracks = useAudioEngineState(s => s.tracks);
  const backend = useStreamingBufferDebugData();
  if (backend.activeBackend !== "streaming" || backend.trackBuffers.length === 0) {
    return null;
  }
  return <Section title="Streaming buffers">
      {backend.trackBuffers.map(trackBuffer => {
      const label = tracks[trackBuffer.trackId]?.label ?? trackBuffer.trackId;
      const fillPercent = trackBuffer.capacitySeconds > 0 ? Math.round(trackBuffer.bufferedSeconds / trackBuffer.capacitySeconds * 100) : 0;
      return <div key={trackBuffer.trackId} className="py-1 border-b border-neutral-700 last:border-b-0 text-[11px]">
            <div className="flex justify-between gap-2">
              <span className="truncate" title={trackBuffer.trackId}>{label}</span>
              <span className="text-cyan-300 shrink-0">{fillPercent}%</span>
            </div>
            <div className="text-[10px] text-neutral-400">
              now {formatBufferDuration(trackBuffer.bufferedSeconds)} · low {formatBufferDuration(trackBuffer.lowWatermarkSeconds)} · cap {formatBufferDuration(trackBuffer.capacitySeconds)}
            </div>
          </div>;
    })}
    </Section>;
});
type DebugRegionRowState = {
  channelCount: number | null;
  codec: string | null;
  duration: number;
  hasAudioBlob: boolean;
  hasAudioBuffer: boolean;
  hasWaveform: boolean;
  hasWaveformKey: boolean;
  id: string;
  pendingUpload: boolean;
  position: number;
  trackId: string;
};
function getRegionStreamingStatusClassName(status: RegionStreamingStatus): string {
  switch (status) {
    case "stream-ready":
      return "text-emerald-300";
    case "streamable-decoded":
      return "text-amber-300";
    case "blocked":
      return "text-rose-300";
  }
}
const RegionDiagnosticsSection = memo(function RegionDiagnosticsSection() {
  const {
    regionMetadata,
    regions,
    tracks,
    waveforms
  } = useAudioEngineDebugData();
  const regionRows = useMemo<DebugRegionRowState[]>(() => Object.values(regions).map(region => {
    const debug = regionMetadata[region.id];
    return {
      channelCount: debug?.channelCount ?? null,
      codec: debug?.codec ?? null,
      duration: region.duration,
      hasAudioBlob: debug?.hasAudioBlob ?? false,
      hasAudioBuffer: debug?.hasAudioBuffer ?? false,
      hasWaveform: waveforms[region.id] !== undefined,
      hasWaveformKey: debug?.hasWaveformKey ?? false,
      id: region.id,
      pendingUpload: region.pendingUpload,
      position: region.position,
      trackId: tracks[region.trackId]?.label ?? region.trackId
    };
  }).sort((a, b) => a.position - b.position || a.trackId.localeCompare(b.trackId) || a.id.localeCompare(b.id)), [regionMetadata, regions, tracks, waveforms]);
  const codecSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const region of regionRows) {
      const codecLabel = region.codec ?? "unknown";
      counts.set(codecLabel, (counts.get(codecLabel) ?? 0) + 1);
    }
    return counts.size === 0 ? "—" : Array.from(counts.entries()).sort(([left], [right]) => left.localeCompare(right)).map(([codec, count]) => `${codec} (${count})`).join(", ");
  }, [regionRows]);
  const streamingStatusCounts = useMemo(() => {
    const counts: Record<RegionStreamingStatus, number> = {
      blocked: 0,
      "stream-ready": 0,
      "streamable-decoded": 0
    };
    for (const region of regionRows) {
      counts[getRegionStreamingDetails(region).status]++;
    }
    return counts;
  }, [regionRows]);
  const blockedCount = streamingStatusCounts.blocked;
  const readyCount = regionRows.length - blockedCount;
  return <Section title={`Region diagnostics (${regionRows.length})`}>
      <Row label="Codecs" value={codecSummary} />
      <Row label="Playback-ready" value={String(readyCount)} />
      <Row label="Needs attention" value={String(blockedCount)} />
      <div className="mt-2">
        {regionRows.length === 0 ? <div className="text-neutral-500 italic">No regions loaded</div> : regionRows.map(region => <RegionDiagnosticsRow key={region.id} region={region} />)}
      </div>
    </Section>;
});
const RegionDiagnosticsRow = memo(function RegionDiagnosticsRow({
  region
}: {
  region: DebugRegionRowState;
}) {
  const streaming = getRegionStreamingDetails(region);
  return <div className="py-1 border-b border-neutral-700 last:border-b-0 text-[11px]">
      <div className="flex justify-between gap-2">
        <span className="truncate" title={region.id}>{region.id}</span>
        <span className="text-cyan-300 shrink-0">
          {region.codec ?? "unknown"}
          {region.channelCount !== null ? ` · ${formatChannelCount(region.channelCount)}` : ""}
        </span>
      </div>
      <div className="flex justify-between gap-2 text-[10px] text-neutral-400">
        <span className="truncate" title={region.trackId}>{region.trackId} @ {formatTime(region.position)}</span>
        <span className={`shrink-0 ${getRegionStreamingStatusClassName(streaming.status)}`}>
          {streaming.label}
        </span>
      </div>
      <div className="text-[10px] text-neutral-500">
        dur {formatTime(region.duration)} · {formatWaveformStatus(region)} · {formatRegionAssetStatus(region)}
      </div>
    </div>;
});
const ListenerCount = memo(function ListenerCount() {
  const engine = useAudioEngine();
  const listenerCount = (engine as unknown as {
    listeners: Set<unknown>;
  }).listeners?.size ?? "?";
  return <span className="text-[10px] text-neutral-400">
      {listenerCount} listeners
    </span>;
});

// ============================================================================
// Memoized components (don't re-render on timestamp changes)
// ============================================================================

interface TrackRowProps {
  id: string;
  state: TrackState;
  hasPendingRegions: boolean;
}
const TrackRow = memo(function TrackRow({
  id,
  state,
  hasPendingRegions
}: TrackRowProps) {
  return <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-2 items-center py-1 border-b border-neutral-700 text-[11px]">
      <span className="truncate" title={id}>
        {state.label || id}
      </span>
      {hasPendingRegions && <span className="w-5 py-0.5 rounded text-center bg-amber-500 text-black font-bold" title="Has pending regions">
          P
        </span>}
      {!hasPendingRegions && <span className="w-5" />}
      <span className={`w-5 py-0.5 rounded text-center ${state.solo ? "bg-green-500 text-black font-bold" : "bg-neutral-700 text-neutral-400"}`}>
        S
      </span>
      <span className={`w-5 py-0.5 rounded text-center ${state.muted ? "bg-red-500 text-white font-bold" : "bg-neutral-700 text-neutral-400"}`}>
        M
      </span>
      <span className={`w-8 py-0.5 rounded text-center text-[10px] ${state.disabled ? "bg-red-500 text-white" : "bg-neutral-700 text-neutral-400"}`}>
        {state.disabled ? "OFF" : "ON"}
      </span>
      <span className="text-neutral-400 min-w-[40px] text-right">
        {Math.round(state.gain * 100)}%
      </span>
    </div>;
});

// ============================================================================
// Tab Bar
// ============================================================================

interface TabBarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
}
const TabBar = memo(function TabBar({
  activeTab,
  onTabChange
}: TabBarProps) {
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: "state",
    label: "State"
  }, {
    id: "commands",
    label: "Commands"
  }];
  return <div className="flex border-b border-neutral-700 bg-neutral-800 shrink-0">
      {tabs.map(tab => <button key={tab.id} onClick={() => onTabChange(tab.id)} className={`px-4 py-1.5 text-[11px] cursor-pointer transition-all duration-150 border-b-2 ${activeTab === tab.id ? "font-bold text-white bg-neutral-700 border-indigo-500" : "font-normal text-neutral-400 bg-transparent border-transparent"}`}>
          {tab.label}
        </button>)}
    </div>;
});

// ============================================================================
// Tab Content: State
// ============================================================================

const StateTabContent = memo(function StateTabContent() {
  return <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 p-3 px-4 overflow-y-auto flex-1 text-neutral-200 text-xs">
      {/* Status & Transport Column */}
      <div>
        <StatusBadges />
        <Section title="Transport">
          <CurrentTimeRow />
          <DurationRow />
          <ProgressRow />
        </Section>
        <Section title="Gain">
          <MasterGainRow />
        </Section>
        <Section title="Pitch">
          <PlaybackRateRow />
          <TransposeRow />
          <PitchShiftBypassRow />
        </Section>
        <StreamingBuffersSection />
        <RecordingSection />
      </div>

      {/* Tracks Column */}
      <div>
        <TracksSection />
      </div>

      {/* Waveforms Column */}
      <div>
        <RegionDiagnosticsSection />
        <WaveformsSection />
      </div>
    </div>;
});

/** Vertical layout for right-docked mode - single column, scrollable */
const StateTabContentVertical = memo(function StateTabContentVertical() {
  return <div className="flex flex-col gap-3 p-3 px-4 overflow-y-auto flex-1 text-neutral-200 text-xs">
      <StatusBadges />
      <Section title="Transport">
        <CurrentTimeRow />
        <DurationRow />
        <ProgressRow />
      </Section>
      <Section title="Gain">
        <MasterGainRow />
      </Section>
      <Section title="Pitch">
        <PlaybackRateRow />
        <TransposeRow />
        <PitchShiftBypassRow />
      </Section>
      <StreamingBuffersSection />
      <RecordingSection />
      <RegionDiagnosticsSection />
      <TracksSection />
      <WaveformsSection />
    </div>;
});

// ============================================================================
// Command Log Hook
// ============================================================================

const EMPTY_LOG: CommandLogEntry[] = [];
function useCommandLog(): CommandLogEntry[] {
  const engine = useAudioEngine();
  const subscribe = useCallback((onStoreChange: () => void) => engine.commandLog?.subscribe(onStoreChange) ?? (() => {}), [engine]);
  const getSnapshot = useCallback(() => engine.commandLog?.getSnapshot() ?? EMPTY_LOG, [engine]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ============================================================================
// Tab Content: Commands
// ============================================================================

function formatTimestamp(ms: number): string {
  const date = new Date(ms);
  return date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }) + "." + date.getMilliseconds().toString().padStart(3, "0");
}
function formatArgs(args?: Record<string, unknown>): string {
  if (!args || Object.keys(args).length === 0) return "";
  return Object.entries(args).map(([key, value]) => {
    if (typeof value === "number") {
      return `${key}: ${Number.isInteger(value) ? value : value.toFixed(3)}`;
    }
    return `${key}: ${value}`;
  }).join(", ");
}
function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === "object") {
    // For TrackState-like objects
    const obj = value as Record<string, unknown>;
    if ("solo" in obj || "muted" in obj || "gain" in obj) {
      const parts: string[] = [];
      if (obj.solo) parts.push("S");
      if (obj.muted) parts.push("M");
      if (typeof obj.gain === "number") parts.push(`${Math.round((obj.gain as number) * 100)}%`);
      if (obj.disabled) parts.push("OFF");
      return parts.length > 0 ? parts.join(" ") : "default";
    }
    return JSON.stringify(value);
  }
  return String(value);
}
function formatDiff(diff?: StateDiff): string {
  if (!diff || Object.keys(diff).length === 0) return "";
  return Object.entries(diff).map(([key, {
    from,
    to
  }]) => {
    const fromStr = formatDiffValue(from);
    const toStr = formatDiffValue(to);
    return `${key}: ${fromStr} → ${toStr}`;
  }).join(", ");
}
const CommandRow = memo(function CommandRow({
  entry
}: {
  entry: CommandLogEntry;
}) {
  const argsStr = formatArgs(entry.args);
  const diffStr = formatDiff(entry.diff);
  const fullTitle = [argsStr, diffStr].filter(Boolean).join(" | ");
  return <div className="grid grid-cols-[80px_auto_1fr] gap-2 py-1 border-b border-neutral-700 text-[11px] items-center">
      <span className="text-neutral-500 font-mono">
        {formatTimestamp(entry.timestamp)}
      </span>
      <span className="text-blue-400 font-medium whitespace-nowrap">
        {entry.command}
      </span>
      <div className="flex gap-2 overflow-hidden min-w-0" title={fullTitle}>
        {argsStr && <span className="text-neutral-400 truncate shrink min-w-0">
            {argsStr}
          </span>}
        {diffStr && <span className="text-green-400 truncate shrink min-w-0">
            {diffStr}
          </span>}
        {!argsStr && !diffStr && <span className="text-neutral-600">—</span>}
      </div>
    </div>;
});
const CommandsTabContent = memo(function CommandsTabContent() {
  const engine = useAudioEngine();
  const commandLog = useCommandLog();
  const handleClear = useCallback(() => {
    engine.commandLog?.clear();
  }, [engine]);

  // Display newest first
  const reversedLog = useMemo(() => [...commandLog].reverse(), [commandLog]);
  return <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-2 border-b border-neutral-700 bg-neutral-800 shrink-0">
        <span className="text-neutral-400 text-[11px]">
          {commandLog.length} command{commandLog.length !== 1 ? "s" : ""}
        </span>
        <button onClick={handleClear} className="px-2 py-1 text-[10px] text-neutral-400 bg-neutral-700 border-none rounded cursor-pointer hover:bg-neutral-600">
          Clear
        </button>
      </div>

      {/* Command list - newest first */}
      <div className="flex-1 overflow-y-auto px-4 py-2">
        {reversedLog.length === 0 ? <div className="text-neutral-500 italic text-xs">
            No commands logged yet. Interact with the player to see commands here.
          </div> : reversedLog.map(entry => <CommandRow key={entry.id} entry={entry} />)}
      </div>
    </div>;
});

// ============================================================================
// Resize Handle
// ============================================================================

interface ResizeHandleProps {
  onResize: (delta: number) => void;
  direction: "vertical" | "horizontal";
}
function ResizeHandle({
  onResize,
  direction
}: ResizeHandleProps) {
  const isDragging = useRef(false);
  const lastPos = useRef(0);
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    lastPos.current = direction === "vertical" ? e.clientY : e.clientX;
    e.preventDefault();
  }, [direction]);
  const onResizeEvent = useEffectEvent((delta: number) => {
    onResize(delta);
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const currentPos = direction === "vertical" ? e.clientY : e.clientX;
      // For vertical (bottom dock): dragging up increases size
      // For horizontal (right dock): dragging left increases size
      const delta = lastPos.current - currentPos;
      lastPos.current = currentPos;
      onResizeEvent(delta);
    };
    const handleMouseUp = () => {
      isDragging.current = false;
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [direction]);
  if (direction === "horizontal") {
    return <div onMouseDown={handleMouseDown} className="w-1.5 cursor-ew-resize flex items-center justify-center bg-neutral-800 border-r border-neutral-700 shrink-0">
        <div className="h-10 w-[3px] bg-neutral-600 rounded" />
      </div>;
  }
  return <div onMouseDown={handleMouseDown} className="h-1.5 cursor-ns-resize flex items-center justify-center bg-neutral-800 border-b border-neutral-700 shrink-0" data-sentry-component="ResizeHandle" data-sentry-source-file="AudioEngineDevTools.tsx">
      <div className="w-10 h-[3px] bg-neutral-600 rounded" />
    </div>;
}

// ============================================================================
// Dock Position Toggle
// ============================================================================

interface DockToggleProps {
  dockPosition: "bottom" | "right";
  onToggle: () => void;
}
const DockToggle = memo(function DockToggle({
  dockPosition,
  onToggle
}: DockToggleProps) {
  return <button onClick={onToggle} className="text-neutral-400 hover:text-white text-xs px-1.5 py-0.5 rounded bg-neutral-700 hover:bg-neutral-600" title={dockPosition === "bottom" ? "Dock to right" : "Dock to bottom"}>
      {dockPosition === "bottom" ? "→" : "↓"}
    </button>;
});
interface HighlightRegionsToggleProps {
  active: boolean;
  onToggle: () => void;
}
const HighlightRegionsToggle = memo(function HighlightRegionsToggle({
  active,
  onToggle
}: HighlightRegionsToggleProps) {
  return <button onClick={onToggle} className={`text-xs px-2 py-0.5 rounded border ${active ? "text-amber-200 bg-amber-950 border-amber-700" : "text-neutral-400 bg-neutral-700 border-neutral-600 hover:bg-neutral-600"}`} title="Toggle highlighted region outlines">
      Regions
    </button>;
});

// ============================================================================
// Main component
// ============================================================================

export function AudioEngineDevTools() {
  const {
    isOpen,
    close,
    dockPosition,
    highlightRegions,
    toggleDockPosition,
    toggleHighlightRegions
  } = useDevTools();
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [activeTab, setActiveTab] = useState<Tab>("state");
  const handleResizeHeight = useCallback((delta: number) => {
    setHeight(prev => Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, prev + delta)));
  }, []);
  const handleResizeWidth = useCallback((delta: number) => {
    setWidth(prev => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, prev + delta)));
  }, []);
  const handleTabChange = useCallback((tab: Tab) => {
    setActiveTab(tab);
  }, []);

  // Don't render anything when closed to avoid unnecessary subscriptions
  if (!isOpen) {
    return null;
  }
  const isRight = dockPosition === "right";

  // Right-docked layout
  if (isRight) {
    return <div className="fixed top-0 right-0 bottom-0 bg-neutral-900 flex flex-row z-[99998] font-mono border-l border-neutral-700 shadow-[-4px_0_12px_rgba(0,0,0,0.5)] overflow-hidden" style={{
      width: `${width}px`
    }}>
        <ResizeHandle onResize={handleResizeWidth} direction="horizontal" />

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-1.5 bg-neutral-800 flex items-center justify-between shrink-0">
            <span className="text-white font-bold text-xs">
              AudioEngine DevTools
            </span>
            <div className="flex items-center gap-2">
              <ListenerCount />
              <HighlightRegionsToggle active={highlightRegions} onToggle={toggleHighlightRegions} />
              <DockToggle dockPosition={dockPosition} onToggle={toggleDockPosition} />
              <button onClick={close} className="text-neutral-400 hover:text-white text-lg leading-none" title="Close DevTools">
                ×
              </button>
            </div>
          </div>

          <TabBar activeTab={activeTab} onTabChange={handleTabChange} />

          {activeTab === "state" && <StateTabContentVertical />}
          {activeTab === "commands" && <CommandsTabContent />}
        </div>
      </div>;
  }

  // Bottom-docked layout (default)
  return <div className="fixed left-0 right-0 bottom-0 bg-neutral-900 flex flex-col z-[99998] font-mono border-t border-neutral-700 shadow-[0_-4px_12px_rgba(0,0,0,0.5)] overflow-hidden" style={{
    height: `${height}px`
  }} data-sentry-component="AudioEngineDevTools" data-sentry-source-file="AudioEngineDevTools.tsx">
      <ResizeHandle onResize={handleResizeHeight} direction="vertical" data-sentry-element="ResizeHandle" data-sentry-source-file="AudioEngineDevTools.tsx" />

      {/* Header */}
      <div className="px-4 py-1.5 bg-neutral-800 flex items-center justify-between shrink-0">
        <span className="text-white font-bold text-xs">
          AudioEngine DevTools
        </span>
        <div className="flex items-center gap-2">
          <ListenerCount data-sentry-element="ListenerCount" data-sentry-source-file="AudioEngineDevTools.tsx" />
          <HighlightRegionsToggle active={highlightRegions} onToggle={toggleHighlightRegions} data-sentry-element="HighlightRegionsToggle" data-sentry-source-file="AudioEngineDevTools.tsx" />
          <DockToggle dockPosition={dockPosition} onToggle={toggleDockPosition} data-sentry-element="DockToggle" data-sentry-source-file="AudioEngineDevTools.tsx" />
          <button onClick={close} className="text-neutral-400 hover:text-white text-lg leading-none" title="Close DevTools">
            ×
          </button>
        </div>
      </div>

      <TabBar activeTab={activeTab} onTabChange={handleTabChange} data-sentry-element="TabBar" data-sentry-source-file="AudioEngineDevTools.tsx" />

      {activeTab === "state" && <StateTabContent />}
      {activeTab === "commands" && <CommandsTabContent />}
    </div>;
}

// ============================================================================
// Shared UI components
// ============================================================================

interface StatusBadgeProps {
  label: string;
  active: boolean;
  activeClassName: string;
}
const StatusBadge = memo(function StatusBadge({
  label,
  active,
  activeClassName
}: StatusBadgeProps) {
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${active ? `${activeClassName} text-white` : "bg-neutral-700 text-neutral-500"}`}>
      {label}
    </span>;
});
interface SectionProps {
  title: string;
  children: React.ReactNode;
}
function Section({
  title,
  children
}: SectionProps) {
  return <div className="mb-3" data-sentry-component="Section" data-sentry-source-file="AudioEngineDevTools.tsx">
      <div className="text-neutral-400 text-[10px] uppercase tracking-wide mb-1.5 border-b border-neutral-700 pb-1">
        {title}
      </div>
      {children}
    </div>;
}
interface RowProps {
  label: string;
  value: string;
}
function formatWaveformStatus(region: DebugRegionRowState): string {
  if (region.hasWaveformKey) {
    return "precalc waveform";
  }
  if (region.hasWaveform) {
    return "generated waveform";
  }
  return "no waveform";
}
function formatChannelCount(channelCount: number): string {
  return `${channelCount} ch`;
}
function formatRegionAssetStatus(region: DebugRegionRowState): string {
  if (region.hasAudioBlob && region.hasAudioBuffer) {
    return "encoded+decoded";
  }
  if (region.hasAudioBlob) {
    return "encoded-only";
  }
  if (region.hasAudioBuffer) {
    return "decoded-only";
  }
  return "no audio";
}
const Row = memo(function Row({
  label,
  value
}: RowProps) {
  return <div className="flex justify-between py-0.5 text-[11px]">
      <span className="text-neutral-400">{label}</span>
      <span className="text-neutral-200 font-mono">{value}</span>
    </div>;
});